import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Alert } from 'antd';
import { 
    ProjectOutlined, 
    ToolOutlined, 
    ClockCircleOutlined, 
    PieChartOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const DashboardStats = () => {
    const [stats, setStats] = useState({
        blocksCount: 0,
        equipmentCount: 0,
        inProgressBlocks: 0,
        totalMoved: { total_soft_moved: 0, total_hard_moved: 0, total_coal_moved: 0 },
        recentWork: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/dashboard/stats');
            // Transform backend response to frontend expected format
            const backendData = response.data;
            const transformedData = {
                blocksCount: backendData.blocks?.total_blocks || 0,
                equipmentCount: backendData.equipment?.total_equipment || 0,
                inProgressBlocks: parseInt(backendData.blocks?.active_blocks) || 0,
                totalMoved: {
                    total_soft_moved: 0, // Not available in current API
                    total_hard_moved: 0, // Not available in current API
                    total_coal_moved: parseFloat(backendData.production?.total_tons_moved) || 0
                },
                recentWork: []
            };
            setStats(transformedData);
            setError(null);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
            setError('Failed to load dashboard statistics');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Block',
            dataIndex: 'block_name',
            key: 'block_name',
        },
        {
            title: 'Operator',
            key: 'operator',
            render: (_, record) => `${record.first_name} ${record.last_name}`,
        },
        {
            title: 'Equipment',
            dataIndex: 'equipment_code',
            key: 'equipment_code',
        },
        {
            title: 'Material',
            dataIndex: 'material_type',
            key: 'material_type',
            render: (text) => text.charAt(0).toUpperCase() + text.slice(1),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (text, record) => `${text} ${record.unit}`,
        },
        {
            title: 'Time',
            dataIndex: 'start_time',
            key: 'start_time',
            render: (text) => new Date(text).toLocaleTimeString(),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Progress type="circle" percent={30} />
                <p>Loading dashboard statistics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error Loading Dashboard"
                description={error}
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 16 }}
            />
        );
    }

    return (
        <div>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Total Blocks"
                            value={stats?.blocksCount || 0}
                            prefix={<ProjectOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Total Equipment"
                            value={stats?.equipmentCount || 0}
                            prefix={<ToolOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Active Projects"
                            value={stats?.inProgressBlocks || 0}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Today's Production"
                            value={((stats?.totalMoved?.total_coal_moved || 0) / 1000).toFixed(1)}
                            suffix="k tons moved"
                            prefix={<PieChartOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={8}>
                    <Card title="Production Summary">
                        <div style={{ marginBottom: 16 }}>
                            <div>Total Volume Moved Today</div>
                            <Statistic
                                value={stats?.totalMoved?.total_coal_moved || 0}
                                suffix="tons"
                            />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <div>Fuel Used Today</div>
                            <Statistic
                                value={0} // TODO: Add fuel data to API
                                suffix="liters"
                            />
                        </div>
                        <div>
                            <div>Equipment Utilization</div>
                            <Progress 
                                percent={stats?.equipmentCount > 0 ? Math.round(((stats?.inProgressBlocks || 0) / stats.equipmentCount) * 100) : 0}
                                strokeColor="#1890ff"
                            />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={16}>
                    <Card title="Recent Activities">
                        {stats.recentWork.length > 0 ? (
                            <Table
                                columns={columns}
                                dataSource={stats.recentWork}
                                rowKey="id"
                                size="small"
                                pagination={false}
                            />
                        ) : (
                            <Alert
                                message="No recent activities"
                                description="Production logs will appear here once recorded."
                                type="info"
                                showIcon
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DashboardStats;